/**
 * ScreenAnalyzer - Rich Context Builder
 *
 * Builds a full picture of what's on screen including semantic roles,
 * layout patterns, and design quality metrics. Replaces the minimal
 * buildScreenContext() for AI prompt generation.
 */

import { CommandColor } from '../commands/CommandSchema';
import { getClosestColorName } from '../commands/ColorMap';
import { FlowObjects } from '../../fsm/types';
import TextFlowObject from '../../../model/TextFlowObject';
import ShapeFlowObject from '../../../model/ShapeFlowObject';
import Screens from '../../../model/Screens';

const CANVAS_WIDTH = 1024;
const CANVAS_HEIGHT = 748;

export type SemanticRole =
  | 'title'
  | 'subtitle'
  | 'body'
  | 'caption'
  | 'hero_image'
  | 'icon'
  | 'background_shape'
  | 'accent_shape'
  | 'divider'
  | 'decoration'
  | 'unknown';

export type LayoutPattern =
  | 'centered_stack'
  | 'left_aligned'
  | 'two_column'
  | 'title_content'
  | 'hero_image'
  | 'scattered';

export interface RichElement {
  index: number;
  mmoId: string;
  type: string;
  semanticRole: SemanticRole;
  text?: string;
  textPreview?: string;
  frame: { x: number; y: number; width: number; height: number };
  center: { x: number; y: number };
  area: number;
  fontSize?: number;
  fontName?: string;
  fontWeight?: 'light' | 'regular' | 'bold' | 'heavy';
  textAlignment?: string;
  textColor?: CommandColor;
  shapeType?: string;
  color?: CommandColor;
  isFullWidth: boolean;
  zPosition: number;
  contrastRatio?: number;
  isReadable?: boolean;
}

export interface DesignMetrics {
  alignmentScore: number;
  spacingConsistency: number;
  contrastScore: number;
  overallScore: number;
}

export interface RichScreenContext {
  screenIndex: number;
  screenSize: { width: number; height: number };
  background: CommandColor;
  backgroundLuminance: number;
  elements: RichElement[];
  layoutPattern: LayoutPattern;
  designMetrics: DesignMetrics;
}

/**
 * Compute relative luminance (0-1) from a color
 */
function getLuminance(color: CommandColor): number {
  return (0.299 * color.r + 0.587 * color.g + 0.114 * color.b) / 255;
}

/**
 * Compute WCAG contrast ratio between two colors
 */
function getContrastRatio(fg: CommandColor, bg: CommandColor): number {
  const l1 = getLuminance(fg);
  const l2 = getLuminance(bg);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Detect font weight from font name string
 */
function detectFontWeight(fontName?: string): 'light' | 'regular' | 'bold' | 'heavy' {
  if (!fontName) return 'regular';
  const lower = fontName.toLowerCase();
  if (lower.includes('heavy') || lower.includes('black') || lower.includes('ultra')) return 'heavy';
  if (lower.includes('bold') || lower.includes('semibold') || lower.includes('demibold')) return 'bold';
  if (lower.includes('light') || lower.includes('thin')) return 'light';
  return 'regular';
}

/**
 * Detect text alignment from MMO
 */
function detectTextAlignment(mmo: TextFlowObject): string {
  if (mmo.textAlignment !== undefined) {
    const align = String(mmo.textAlignment);
    if (align === '1') return 'center';
    if (align === '2') return 'right';
  }
  return 'left';
}

/**
 * Build a RichElement from an MMO
 */
function buildRichElement(
  mmo: FlowObjects,
  index: number,
  background: CommandColor
): RichElement {
  const frame = mmo.frame;
  const area = frame.width * frame.height;
  const canvasArea = CANVAS_WIDTH * CANVAS_HEIGHT;

  const element: RichElement = {
    index,
    mmoId: mmo.id,
    type: mmo.type,
    semanticRole: 'unknown',
    frame: { x: frame.x, y: frame.y, width: frame.width, height: frame.height },
    center: { x: frame.x + frame.width / 2, y: frame.y + frame.height / 2 },
    area,
    isFullWidth: frame.width > CANVAS_WIDTH * 0.8,
    zPosition: index,
  };

  if (mmo.type === 'text') {
    const textMmo = mmo as TextFlowObject;
    const fullText = textMmo.text || '';
    element.text = fullText;
    element.textPreview = fullText.length > 80 ? fullText.substring(0, 80) + '...' : fullText;
    element.fontSize = textMmo.font?.fontSize;
    element.fontName = textMmo.font?.fontName;
    element.fontWeight = detectFontWeight(textMmo.font?.fontName);
    element.textAlignment = detectTextAlignment(textMmo);
    if (textMmo.textColor) {
      element.textColor = {
        r: textMmo.textColor.r,
        g: textMmo.textColor.g,
        b: textMmo.textColor.b,
        a: textMmo.textColor.a,
      };
      element.contrastRatio = getContrastRatio(element.textColor, background);
      element.isReadable = element.contrastRatio >= 3;
    }
  } else if (mmo.type === 'shape') {
    const shapeMmo = mmo as ShapeFlowObject;
    element.shapeType = shapeMmo.shapeType;
    if (shapeMmo.color) {
      element.color = {
        r: shapeMmo.color.r,
        g: shapeMmo.color.g,
        b: shapeMmo.color.b,
        a: shapeMmo.color.a,
      };
    }
  } else if (mmo.type === 'image') {
    // Image-specific properties are already captured by frame/area
  }

  return element;
}

/**
 * Assign semantic roles to elements based on their properties and relationships
 */
function assignSemanticRoles(elements: RichElement[]): void {
  const textElements = elements.filter((e) => e.type === 'text');
  const imageElements = elements.filter((e) => e.type === 'image');
  const shapeElements = elements.filter((e) => e.type === 'shape');
  const canvasArea = CANVAS_WIDTH * CANVAS_HEIGHT;

  // Sort text by font size descending
  const sortedText = [...textElements].sort(
    (a, b) => (b.fontSize || 0) - (a.fontSize || 0)
  );

  // Assign text roles
  for (let i = 0; i < sortedText.length; i++) {
    const el = sortedText[i];
    const wordCount = (el.text || '').split(/\s+/).filter(Boolean).length;
    const isTop = el.center.y < CANVAS_HEIGHT * 0.4;
    const isBold = el.fontWeight === 'bold' || el.fontWeight === 'heavy';

    if (
      i === 0 &&
      (el.fontSize || 0) >= 28 &&
      wordCount < 15 &&
      isTop
    ) {
      el.semanticRole = 'title';
    } else if (
      i === 0 &&
      (el.fontSize || 0) >= 28 &&
      wordCount < 15
    ) {
      // Large text not at top, still likely title
      el.semanticRole = 'title';
    } else if (
      i === 1 &&
      sortedText[0]?.semanticRole === 'title' &&
      Math.abs(el.center.y - (sortedText[0].frame.y + sortedText[0].frame.height)) < 100
    ) {
      el.semanticRole = 'subtitle';
    } else if (wordCount <= 5 && (el.fontSize || 0) <= 14) {
      el.semanticRole = 'caption';
    } else {
      el.semanticRole = 'body';
    }
  }

  // Assign image roles
  for (const el of imageElements) {
    if (el.area > canvasArea * 0.4) {
      el.semanticRole = 'hero_image';
    } else if (el.frame.width < 120 && el.frame.height < 120) {
      el.semanticRole = 'icon';
    } else {
      el.semanticRole = 'decoration';
    }
  }

  // Assign shape roles
  for (const el of shapeElements) {
    if (el.zPosition === 0 && el.area > canvasArea * 0.6) {
      el.semanticRole = 'background_shape';
    } else if (el.area < canvasArea * 0.15) {
      el.semanticRole = 'accent_shape';
    } else if (
      (el.frame.height < 10 && el.frame.width > CANVAS_WIDTH * 0.3) ||
      (el.frame.width < 10 && el.frame.height > CANVAS_HEIGHT * 0.3)
    ) {
      el.semanticRole = 'divider';
    } else {
      el.semanticRole = 'decoration';
    }
  }
}

/**
 * Detect the overall layout pattern of the screen
 */
function detectLayoutPattern(elements: RichElement[]): LayoutPattern {
  if (elements.length === 0) return 'scattered';

  const visibleElements = elements.filter(
    (e) => e.semanticRole !== 'background_shape'
  );
  if (visibleElements.length === 0) return 'scattered';

  const centers = visibleElements.map((e) => e.center);
  const canvasCenterX = CANVAS_WIDTH / 2;

  // Check if all centers are near canvas center X
  const allCentered = centers.every(
    (c) => Math.abs(c.x - canvasCenterX) < 100
  );
  if (allCentered && visibleElements.length >= 2) return 'centered_stack';

  // Check for two-column layout
  const leftGroup = centers.filter((c) => c.x < CANVAS_WIDTH * 0.4);
  const rightGroup = centers.filter((c) => c.x > CANVAS_WIDTH * 0.6);
  if (leftGroup.length >= 1 && rightGroup.length >= 1) return 'two_column';

  // Check for hero image
  const heroImage = elements.find((e) => e.semanticRole === 'hero_image');
  if (heroImage && heroImage.area > CANVAS_WIDTH * CANVAS_HEIGHT * 0.5) {
    return 'hero_image';
  }

  // Check for title + content
  const hasTitle = elements.some((e) => e.semanticRole === 'title');
  const hasBody = elements.some(
    (e) => e.semanticRole === 'body' || e.semanticRole === 'subtitle'
  );
  if (hasTitle && hasBody) return 'title_content';

  // Check left alignment
  const allLeft = visibleElements.every((e) => e.frame.x < CANVAS_WIDTH * 0.3);
  if (allLeft) return 'left_aligned';

  return 'scattered';
}

/**
 * Compute design quality metrics
 */
function computeDesignMetrics(
  elements: RichElement[],
  background: CommandColor
): DesignMetrics {
  const visibleElements = elements.filter(
    (e) => e.semanticRole !== 'background_shape'
  );

  // Alignment score: check if edges cluster on common axes
  let alignmentScore = 1;
  if (visibleElements.length >= 2) {
    const leftEdges = visibleElements.map((e) => e.frame.x);
    const centerXs = visibleElements.map((e) => e.center.x);
    const rightEdges = visibleElements.map((e) => e.frame.x + e.frame.width);

    const bestAlignment = Math.min(
      normalizedStdDev(leftEdges),
      normalizedStdDev(centerXs),
      normalizedStdDev(rightEdges)
    );
    // Lower std dev = better alignment. Map 0-200 range to 1-0 score.
    alignmentScore = Math.max(0, 1 - bestAlignment / 200);
  }

  // Spacing consistency: for vertically stacked elements
  let spacingConsistency = 1;
  const sorted = [...visibleElements].sort((a, b) => a.center.y - b.center.y);
  if (sorted.length >= 3) {
    const gaps: number[] = [];
    for (let i = 1; i < sorted.length; i++) {
      const gap = sorted[i].frame.y - (sorted[i - 1].frame.y + sorted[i - 1].frame.height);
      gaps.push(gap);
    }
    const meanGap = gaps.reduce((s, g) => s + g, 0) / gaps.length;
    if (meanGap > 0) {
      const stdDev = Math.sqrt(
        gaps.reduce((s, g) => s + Math.pow(g - meanGap, 2), 0) / gaps.length
      );
      spacingConsistency = Math.max(0, 1 - stdDev / meanGap);
    }
  }

  // Contrast score: minimum contrast ratio across text elements
  let contrastScore = 1;
  const textElements = elements.filter((e) => e.type === 'text' && e.contrastRatio !== undefined);
  if (textElements.length > 0) {
    const minContrast = Math.min(...textElements.map((e) => e.contrastRatio!));
    // WCAG AA requires 4.5:1 for normal text, 3:1 for large
    // Map: 1:1 → 0, 4.5:1 → 1
    contrastScore = Math.min(1, Math.max(0, (minContrast - 1) / 3.5));
  }

  const overallScore = (alignmentScore + spacingConsistency + contrastScore) / 3;

  return { alignmentScore, spacingConsistency, contrastScore, overallScore };
}

function normalizedStdDev(values: number[]): number {
  if (values.length < 2) return 0;
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  return Math.sqrt(
    values.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / values.length
  );
}

/**
 * Build a rich screen context for AI prompt generation
 */
export function analyzeScreen(
  screen: Screens,
  screenIndex: number,
  canvasSize?: { width: number; height: number }
): RichScreenContext {
  const bgColor = screen.FlowScreenModelBackgroundColorKey;
  const background: CommandColor = bgColor
    ? { r: bgColor.r, g: bgColor.g, b: bgColor.b, a: bgColor.a }
    : { r: 255, g: 255, b: 255, a: 1 };

  const screenSize = canvasSize || { width: CANVAS_WIDTH, height: CANVAS_HEIGHT };
  const mmos = screen.mixedMediaObjects || [];
  const elements = mmos.map((mmo, i) => buildRichElement(mmo, i, background));

  assignSemanticRoles(elements);

  const layoutPattern = detectLayoutPattern(elements);
  const designMetrics = computeDesignMetrics(elements, background);

  return {
    screenIndex,
    screenSize,
    background,
    backgroundLuminance: getLuminance(background),
    elements,
    layoutPattern,
    designMetrics,
  };
}

/**
 * Format rich context as a human-readable string for LLM prompts
 */
export function formatContextForPrompt(ctx: RichScreenContext): string {
  const bgName = getClosestColorName(ctx.background);
  const bgTone = ctx.backgroundLuminance < 0.5 ? 'dark' : 'light';

  let out = `CURRENT SCREEN:\n`;
  out += `Background: ${bgName} ({r:${ctx.background.r},g:${ctx.background.g},b:${ctx.background.b},a:${ctx.background.a}}), luminance: ${ctx.backgroundLuminance.toFixed(2)} (${bgTone})\n`;
  out += `Layout: ${ctx.layoutPattern}\n`;
  out += `Design score: alignment=${ctx.designMetrics.alignmentScore.toFixed(1)}, spacing=${ctx.designMetrics.spacingConsistency.toFixed(1)}, contrast=${ctx.designMetrics.contrastScore.toFixed(1)}\n`;
  out += `\nELEMENTS:\n`;

  for (const el of ctx.elements) {
    const roleLabel = el.semanticRole.toUpperCase();
    let line = `[${el.index}] ${roleLabel}`;

    if (el.type === 'text') {
      const preview = el.textPreview || '';
      line += ` "${preview}"`;
      if (el.fontSize) line += ` | ${el.fontSize}pt`;
      if (el.fontName) line += ` ${el.fontName}`;
      if (el.fontWeight && el.fontWeight !== 'regular') line += `-${el.fontWeight}`;
      if (el.textColor) {
        const colorName = getClosestColorName(el.textColor);
        line += ` | ${colorName}`;
      }
      if (el.textAlignment) line += ` | ${el.textAlignment}-aligned`;
    } else if (el.type === 'shape') {
      if (el.color) {
        const colorName = getClosestColorName(el.color);
        line += ` ${colorName} ${el.shapeType || 'shape'}`;
      } else {
        line += ` ${el.shapeType || 'shape'}`;
      }
    } else if (el.type === 'image') {
      line += ` image`;
    } else {
      line += ` ${el.type}`;
    }

    line += `\n    Position: (${Math.round(el.frame.x)}, ${Math.round(el.frame.y)}) Size: ${Math.round(el.frame.width)}x${Math.round(el.frame.height)} | Role: ${el.semanticRole}`;
    if (el.contrastRatio !== undefined) {
      line += ` | Contrast: ${el.contrastRatio.toFixed(1)}:1${el.isReadable ? '' : ' (LOW)'}`;
    }

    out += line + '\n';
  }

  return out;
}
